/**
 * Request Deduplication System
 * 
 * Prevents duplicate concurrent requests for the same resource
 * by tracking in-flight requests and sharing their results.
 */

interface PendingRequest<T> {
  promise: Promise<T>;
  timestamp: number;
}

class RequestDeduplicator {
  private pendingRequests = new Map<string, PendingRequest<any>>();
  private readonly REQUEST_TIMEOUT = 30000; // 30 seconds

  /**
   * Execute a request with deduplication
   * If the same request is already in flight, returns the existing promise
   */
  async deduplicate<T>(
    key: string,
    requestFn: () => Promise<T>
  ): Promise<T> {
    // Check if request is already pending
    const existing = this.pendingRequests.get(key);
    if (existing) {
      // Check if request hasn't timed out
      if (Date.now() - existing.timestamp < this.REQUEST_TIMEOUT) {
        return existing.promise;
      } else {
        // Request timed out, remove it
        this.pendingRequests.delete(key);
      }
    }

    // Create new request
    const promise = requestFn()
      .then(result => {
        // Remove from pending after completion
        this.pendingRequests.delete(key);
        return result;
      })
      .catch(error => {
        // Remove from pending on error
        this.pendingRequests.delete(key);
        throw error;
      });

    // Store pending request
    this.pendingRequests.set(key, {
      promise,
      timestamp: Date.now()
    });

    return promise;
  }

  /**
   * Clear all pending requests (useful for cleanup)
   */
  clear() {
    this.pendingRequests.clear();
  }

  /**
   * Remove a specific pending request
   */
  remove(key: string) {
    this.pendingRequests.delete(key);
  }

  /**
   * Get count of pending requests
   */
  getPendingCount(): number {
    return this.pendingRequests.size;
  }
}

// Singleton instance
export const requestDeduplicator = new RequestDeduplicator();

